import { memo } from 'react';
import ShieldIcon from '@mui/icons-material/Shield';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { readableDropDareDifficultyLevels } from 'root/src/shared/constants/dropEvent';
import { DareCta, DARE_DIFFICULTY_CLASS, DlcChip, ClipsOnlyChip, dareRewardName, isDlcDareReward, isClipsOnlyDare } from 'root/src/client/v2/_pages/drop/redesign/dareCta';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { upscaleBoxArt } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { RewardRemaining, isUncountableReward } from 'root/src/client/v2/common/components/RewardRemaining';
import { RewardAmountBadge } from 'root/src/client/v2/common/components/RewardAmountBadge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
// The reward block: what the creator gets, how many of it, and how many are left. The amount is a
// tinted "×N" badge: creators could not see the previous 10px inline badge, and a bare figure did not
// read as a quantity. The badge chrome carries the emphasis, so the number itself stays moderate.
// A tooltip stands in for a label, and mirrors the legacy RewardScalableIndicator
// (web/list/listItemComponents/dare/DareCardBody.js) when the reward scales with viewership.
// The bundle and amount tooltips sit on their own elements rather than wrapping the whole block:
// nesting one Radix trigger inside another pops both tooltips on a single hover.
const DareReward = memo(({ rewardName, amount, capacity, isUnlimited, isRewardScalable, bundleRewards, isDlc }) => {
    const isBundle = Boolean(bundleRewards === null || bundleRewards === void 0 ? void 0 : bundleRewards.length);
    return (<div className="mt-3 border-t border-solid border-white/[0.06] pt-3">
			<TooltipProvider delayDuration={150}>
				<div className="flex items-start justify-between gap-3">
					<div className="flex min-w-0 flex-1 items-start gap-2">
						<CardGiftcardIcon sx={{ fontSize: 15 }} className={cn('mt-0.5 shrink-0', isDlc ? 'text-neon-teal' : 'text-neon-green')}/>
						{isBundle ? (<Tooltip>
								<TooltipTrigger asChild>
									<p className="flex min-w-0 cursor-help items-center gap-1 text-xs font-medium text-foreground/90">
										<span className="truncate">{rewardName}</span>
										<InfoOutlinedIcon sx={{ fontSize: 13 }} className="shrink-0 text-muted-foreground"/>
									</p>
								</TooltipTrigger>
								<TooltipContent className="max-w-[240px] p-0">
									<p className="border-b border-solid border-white/[0.06] px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
										Bundle includes
									</p>
									<ul className="flex flex-col gap-1 p-2">
										{(bundleRewards !== null && bundleRewards !== void 0 ? bundleRewards : []).map(reward => (<li key={reward.id} className="text-xs text-foreground/90">
												<span className="font-semibold text-foreground">({reward.amount})</span> {reward.name}
											</li>))}
									</ul>
								</TooltipContent>
							</Tooltip>) : (
        // Tooltip carries the untruncated name: long reward names (DLCs especially) get cut
        // off by the card width and there was no way to read the full thing.
        <Tooltip>
								<TooltipTrigger asChild>
									<p className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-foreground/90">
										<span className="truncate">{rewardName}</span>
										{isDlc && <DlcChip />}
									</p>
								</TooltipTrigger>
								<TooltipContent className="max-w-[240px]">{rewardName}</TooltipContent>
							</Tooltip>)}
					</div>
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="flex shrink-0 cursor-help items-center gap-1">
								<RewardAmountBadge amount={amount}/>
								{isRewardScalable && <InfoOutlinedIcon sx={{ fontSize: 12 }} className="shrink-0 text-muted-foreground"/>}
							</div>
						</TooltipTrigger>
						<TooltipContent className="max-w-[220px]">
							{isRewardScalable
            ? 'Rewards you get for completing this dare. This amount scales with your average CCV/Views.'
            : 'Rewards you get for completing this dare.'}
						</TooltipContent>
					</Tooltip>
				</div>
			</TooltipProvider>
			<RewardRemaining capacity={capacity} isUnlimited={isUnlimited} className="mt-1.5"/>
		</div>);
});
export const RedesignDareCard = memo(({ drop, dare, refetchDropEvent }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const image = buildAssetUrl(upscaleBoxArt((_c = (_b = (_a = dare.games) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.image) !== null && _c !== void 0 ? _c : (_d = drop.gameObj) === null || _d === void 0 ? void 0 : _d.image, '600x800'), { w: 600, q: 80, fmt: 'webp' });
    const rewardName = dareRewardName(dare);
    const bundleRewards = ((_e = dare.product) === null || _e === void 0 ? void 0 : _e.type) === 'bundle' ? dare.product.rewards : undefined;
    const isUnlimitedReward = isUncountableReward((_f = dare.product) === null || _f === void 0 ? void 0 : _f.type);
    return (<article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-solid border-white/[0.06] bg-card transition-colors hover:border-neon-pink/30">
			<div className="relative aspect-[16/10] overflow-hidden">
				{image && <Img src={image} alt={dare.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"/>}
				<div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent opacity-70"/>
				<span className={cn('absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-md border border-solid bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider backdrop-blur-md', DARE_DIFFICULTY_CLASS)}>
					<ShieldIcon sx={{ fontSize: 12 }}/>
					{(_g = readableDropDareDifficultyLevels[dare.level]) !== null && _g !== void 0 ? _g : dare.level}
				</span>
			</div>

			<div className="flex flex-1 flex-col p-4">
				<h4 className="line-clamp-1 text-sm font-bold text-foreground">{dare.title}</h4>
				{/* min-h-8 reserves the full two clamped lines even for one-line descriptions, so the
        platform row and everything below it sits at the same offset on every card in a row. */}
				<p className="mt-0.5 line-clamp-2 min-h-8 text-xs text-muted-foreground">{dare.description}</p>

				{/* The clips-only chip sits with the platform icons because the restriction is a Twitch
        delivery rule; it is shorter than the 20px icons, so the row keeps its height either way. */}
				{((_h = dare.platforms) === null || _h === void 0 ? void 0 : _h.length) > 0 && (<div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
						<ul className="flex items-center gap-1.5">
							{dare.platforms.map(platform => (<li key={platform} className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
									<DarePlatformIcon size="12" platform={platform} color={PlatformIconColor.Dark}/>
								</li>))}
						</ul>
						{isClipsOnlyDare(dare) && <ClipsOnlyChip />}
					</div>)}

				{/* Reward + CTA anchor to the card bottom as one group (the grid's auto-rows-fr equalises
        card heights), so the reward meter and the button line up across the row; any leftover
        space collects between the platforms row and this block instead of shifting it around. */}
				<div className="mt-auto">
					<DareReward rewardName={rewardName} amount={dare.amount} capacity={dare.capacity} isUnlimited={isUnlimitedReward} isRewardScalable={Boolean(dare.isRewardScalable)} bundleRewards={bundleRewards} isDlc={isDlcDareReward(dare)}/>

					{/* pt-4 (not mt-4 on each control) since the CTA controls moved to the shared DareCta,
        which renders margin-free so the list row can size them differently. */}
					<div className="pt-4">
						<DareCta drop={drop} dare={dare} refetchDropEvent={refetchDropEvent}/>
					</div>
				</div>
			</div>
		</article>);
});
